import React from "react";
import { cn } from "@kkp/app-kit";

export type PageContainerProps = React.HTMLAttributes<HTMLDivElement> & {
  /**
   * Page title to render at the top of the content area.
   * If you already render a title in the Topbar, you can omit this.
   */
  title?: React.ReactNode;

  /**
   * Optional description shown under the title (muted).
   */
  description?: React.ReactNode;

  /**
   * Right-side actions area (e.g. buttons, filters).
   */
  actions?: React.ReactNode;

  /**
   * Controls the max width of the container.
   * - `default`: good for forms/content
   * - `wide`: good for tables/lists
   * - `full`: no max width
   */
  width?: "default" | "wide" | "full";

  /**
   * Controls padding.
   */
  padding?: "none" | "sm" | "md" | "lg";

  /**
   * Controls vertical spacing between header and content.
   */
  gap?: "sm" | "md" | "lg";
};

/**
 * PageContainer
 *
 * Goal: enforce consistent spacing, width, and header layout across pages,
 * aligned with the login page look (dark glass surfaces, muted helper text).
 *
 * Intended usage (inside the dashboard main content area):
 *   <PageContainer
 *     title="Clients"
 *     description="Search and manage client companies."
 *     actions={<Button>...</Button>}
 *   >
 *     ...
 *   </PageContainer>
 */
export function PageContainer({
  title,
  description,
  actions,
  width = "full",
  padding = "md",
  gap = "md",
  className,
  children,
  ...props
}: PageContainerProps) {
  const widthClass =
    width === "full"
      ? "max-w-none"
      : width === "wide"
        ? "max-w-[1200px]"
        : "max-w-[960px]";

  const paddingClass =
    padding === "none"
      ? "p-0"
      : padding === "sm"
        ? "p-3"
        : padding === "lg"
          ? "p-6"
          : "p-4";

  const gapClass = gap === "sm" ? "gap-3" : gap === "lg" ? "gap-6" : "gap-4";

  const showHeader = Boolean(title || description || actions);

  return (
    <div
      className={cn("w-full", widthClass, "mx-auto", paddingClass, className)}
      {...props}
    >
      <div className={cn("grid", gapClass)}>
        {showHeader ? (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              {title ? (
                <div className="text-sm font-bold text-[color:rgba(255,255,255,0.92)]">
                  {title}
                </div>
              ) : null}

              {description ? (
                <div className="mt-1 text-xs text-[color:var(--kkp-text-muted)] leading-snug">
                  {description}
                </div>
              ) : null}
            </div>

            {actions ? (
              <div className="flex shrink-0 flex-col gap-2 sm:flex-row sm:items-center">
                {actions}
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="min-w-0">{children}</div>
      </div>
    </div>
  );
}
